import {
  clearAdsAttributionCookie,
  getAdsAttributionCookie,
  setAdsAttributionCookie,
} from "./adsAttributionCookie";
import { installAdsRequestSanitization } from "./adsRequestSanitization";
import {
  getLinkedInAdsClickId,
  isLinkedInAdsRolloutCountry,
  LINKEDIN_ADS_CLICK_ID_QUERY_PARAM,
  LINKEDIN_ADS_CLICK_ID_STORAGE_KEY,
} from "./linkedInAdsClickId";

const LINKEDIN_INSIGHT_SCRIPT_ID = "linkedin-ads-insight";
const LINKEDIN_INSIGHT_SCRIPT_URL = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
const LINKEDIN_MEASUREMENT_READY_TIMEOUT_MS = 1500;
const LINKEDIN_SCRIPT_LOAD_TIMEOUT_MS = 1500;
const LINKEDIN_REQUEST_SANITIZATION_MARKER = "__tapestryLinkedInAdsRequestSanitizationInstalled";
const RESTRICTED_PAGE_URL = "https://ads.openai.com/";
const LINKEDIN_CLICK_ID_COOKIE_MAX_AGE_DAYS = 30;
const MAX_NESTED_URL_DEPTH = 3;
const LINKEDIN_PENDING_CONVERSIONS_STORAGE_KEY =
  "tapestry:linkedInAds:pendingAccountCreationConversions";
const LINKEDIN_PENDING_CLICK_IDS_STORAGE_KEY =
  "tapestry:linkedInAds:pendingAccountCreationConversionClickIds";
const LINKEDIN_TRACKED_CONVERSIONS_STORAGE_KEY =
  "tapestry:linkedInAds:trackedAccountCreationConversions";
const MAX_STORED_TRANSACTION_IDS = 20;

type Lintrk = ((action: string, data: Record<string, unknown>) => void) & {
  q?: Array<[string, Record<string, unknown>]>;
};

declare global {
  interface Window {
    _linkedin_data_partner_ids?: string[];
    _linkedin_partner_id?: string;
    lintrk?: Lintrk;
  }
}

let measurementEnabled = false;
let consentResolved = true;
let partnerId: string | undefined;
let accountCreationConversionId: string | undefined;
let shareAllConversions = false;
let measurementReadyWaiters: Array<(ready: boolean) => void> = [];
let scriptLoaded = false;
let scriptLoadPromise: Promise<boolean> | undefined;
const inFlightTransactionIds = new Set<string>();
const trackedTransactionIds = new Set<string>();

export { isLinkedInAdsRolloutCountry };

export function setLinkedInAdsMeasurementState({
  enabled,
  isConsentResolved,
  shouldClearAttribution = false,
  shouldShareAllConversions = false,
  configuredPartnerId,
  configuredAccountCreationConversionId,
}: {
  enabled: boolean;
  isConsentResolved: boolean;
  shouldClearAttribution?: boolean;
  shouldShareAllConversions?: boolean;
  configuredPartnerId: string;
  configuredAccountCreationConversionId: string;
}) {
  measurementEnabled = enabled;
  consentResolved = isConsentResolved;
  shareAllConversions = shouldShareAllConversions;
  partnerId = normalizeNumericId(configuredPartnerId);
  accountCreationConversionId = normalizeNumericId(configuredAccountCreationConversionId);

  if (measurementEnabled && typeof window !== "undefined") {
    captureLinkedInAdsClickIdFromCurrentUrl();
    void drainPendingLinkedInAdsConversions();
  } else if (typeof window !== "undefined" && shouldClearAttribution) {
    clearStoredLinkedInAdsClickId();
    clearPendingLinkedInAdsConversions();
    removeLinkedInAdsClickIdFromCurrentUrl();
    clearLinkedInAttributionCookie();
    clearLinkedInInsightTagState();
  }
  notifyMeasurementStateChanged();
}

export async function trackLinkedInAdsAccountCreated(transactionId: string): Promise<void> {
  if (!transactionId || hasTrackedTransactionId(transactionId)) return;
  let clickId = getStoredLinkedInAdsClickId();
  if (!isMeasurementReady()) {
    if (!(await waitForMeasurementReady())) return;
    clickId = getStoredLinkedInAdsClickId();
  }
  if (!clickId && !shareAllConversions) return;

  // Persist synchronously so an immediate post-create navigation cannot lose the conversion.
  storePendingTransactionId(transactionId, clickId);
  if (!isMeasurementReady() && !(await waitForMeasurementReady())) return;

  if (!partnerId || !accountCreationConversionId) return;
  await drainPendingLinkedInAdsConversions();
}

async function drainPendingLinkedInAdsConversions(): Promise<void> {
  if (!isMeasurementReady()) return;
  const targetPartnerId = partnerId;
  const targetConversionId = accountCreationConversionId;
  if (!targetPartnerId || !targetConversionId) return;

  const transactionIds = getStoredTransactionIds(LINKEDIN_PENDING_CONVERSIONS_STORAGE_KEY);
  await Promise.all(
    transactionIds.map((transactionId) => {
      const clickId = getPendingClickId(transactionId, getStoredLinkedInAdsClickId());
      if (!clickId && !shareAllConversions) return Promise.resolve();
      return deliverLinkedInAdsConversion({
        transactionId,
        clickId,
        targetPartnerId,
        targetConversionId,
      });
    }),
  );
}

async function deliverLinkedInAdsConversion({
  transactionId,
  clickId,
  targetPartnerId,
  targetConversionId,
}: {
  transactionId: string;
  clickId: string | undefined;
  targetPartnerId: string;
  targetConversionId: string;
}): Promise<void> {
  if (hasTrackedTransactionId(transactionId) || inFlightTransactionIds.has(transactionId)) return;
  inFlightTransactionIds.add(transactionId);
  try {
    installAdsRequestSanitization({
      marker: LINKEDIN_REQUEST_SANITIZATION_MARKER,
      rewriteUrl: rewriteLinkedInAdsRequestUrl,
    });
    if (clickId) {
      ensureLinkedInAttributionCookie(clickId);
    } else {
      clearLinkedInAttributionCookie();
    }
    const lintrk = initializeLinkedInInsightTag(targetPartnerId);

    if (
      !(await loadLinkedInInsightScript()) ||
      !measurementEnabled ||
      (!clickId && !shareAllConversions) ||
      partnerId !== targetPartnerId ||
      accountCreationConversionId !== targetConversionId
    ) {
      return;
    }

    const loadedLintrk = window.lintrk ?? lintrk;
    loadedLintrk("track", { conversion_id: Number(targetConversionId) });
    restoreCurrentLinkedInAttributionCookie(clickId);
    markTransactionIdTracked(transactionId);
  } catch (error) {
    console.warn("Failed to track LinkedIn Ads account creation conversion", { error });
  } finally {
    inFlightTransactionIds.delete(transactionId);
  }
}

function getStoredTransactionIds(storageKey: string): string[] {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(storageKey) ?? "[]");
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is string => typeof item === "string" && item.length > 0);
  } catch {
    return [];
  }
}

function setStoredTransactionIds(storageKey: string, transactionIds: string[]) {
  try {
    window.sessionStorage.setItem(
      storageKey,
      JSON.stringify(transactionIds.slice(-MAX_STORED_TRANSACTION_IDS)),
    );
  } catch {
    // Measurement storage failures must not block account creation.
  }
}

function storePendingTransactionId(transactionId: string, clickId: string | undefined) {
  const pending = getStoredTransactionIds(LINKEDIN_PENDING_CONVERSIONS_STORAGE_KEY);
  if (pending.includes(transactionId)) return;

  const nextPending = [...pending, transactionId].slice(-MAX_STORED_TRANSACTION_IDS);
  setStoredTransactionIds(LINKEDIN_PENDING_CONVERSIONS_STORAGE_KEY, nextPending);
  setPendingClickId(transactionId, clickId, nextPending);
}

function getPendingClickIds(): Record<string, string | null> {
  try {
    const value = JSON.parse(
      window.sessionStorage.getItem(LINKEDIN_PENDING_CLICK_IDS_STORAGE_KEY) ?? "{}",
    );
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    return Object.fromEntries(
      Object.entries(value).filter(
        (entry): entry is [string, string | null] =>
          typeof entry[0] === "string" && (typeof entry[1] === "string" || entry[1] === null),
      ),
    );
  } catch {
    return {};
  }
}

function setPendingClickId(
  transactionId: string,
  clickId: string | undefined,
  pendingTransactionIds: string[],
) {
  try {
    const pendingClickIds = Object.fromEntries(
      Object.entries(getPendingClickIds()).filter(([pendingId]) =>
        pendingTransactionIds.includes(pendingId),
      ),
    );
    pendingClickIds[transactionId] = clickId ?? null;
    window.sessionStorage.setItem(
      LINKEDIN_PENDING_CLICK_IDS_STORAGE_KEY,
      JSON.stringify(pendingClickIds),
    );
  } catch {
    // Measurement storage failures must not block account creation.
  }
}

function getPendingClickId(
  transactionId: string,
  legacyClickId: string | undefined,
): string | undefined {
  const pendingClickIds = getPendingClickIds();
  return Object.prototype.hasOwnProperty.call(pendingClickIds, transactionId) ?
      pendingClickIds[transactionId] ?? undefined
    : legacyClickId;
}

function hasTrackedTransactionId(transactionId: string): boolean {
  return (
    trackedTransactionIds.has(transactionId) ||
    getStoredTransactionIds(LINKEDIN_TRACKED_CONVERSIONS_STORAGE_KEY).includes(transactionId)
  );
}

function markTransactionIdTracked(transactionId: string) {
  trackedTransactionIds.add(transactionId);
  const tracked = getStoredTransactionIds(LINKEDIN_TRACKED_CONVERSIONS_STORAGE_KEY);
  if (!tracked.includes(transactionId)) {
    setStoredTransactionIds(LINKEDIN_TRACKED_CONVERSIONS_STORAGE_KEY, [...tracked, transactionId]);
  }
  setStoredTransactionIds(
    LINKEDIN_PENDING_CONVERSIONS_STORAGE_KEY,
    getStoredTransactionIds(LINKEDIN_PENDING_CONVERSIONS_STORAGE_KEY).filter(
      (pendingId) => pendingId !== transactionId,
    ),
  );
  const pendingClickIds = getPendingClickIds();
  delete pendingClickIds[transactionId];
  try {
    window.sessionStorage.setItem(
      LINKEDIN_PENDING_CLICK_IDS_STORAGE_KEY,
      JSON.stringify(pendingClickIds),
    );
  } catch {
    // Measurement storage failures must not block account creation.
  }
}

function clearPendingLinkedInAdsConversions() {
  try {
    window.sessionStorage.removeItem(LINKEDIN_PENDING_CONVERSIONS_STORAGE_KEY);
    window.sessionStorage.removeItem(LINKEDIN_PENDING_CLICK_IDS_STORAGE_KEY);
  } catch {
    // Consent revocation must not fail when storage is unavailable.
  }
  inFlightTransactionIds.clear();
}

export function captureLinkedInAdsClickIdFromCurrentUrl() {
  if (!measurementEnabled || typeof window === "undefined") return;

  const searchParams = new URLSearchParams(window.location.search);
  if (!searchParams.has(LINKEDIN_ADS_CLICK_ID_QUERY_PARAM)) return;

  const clickId = getLinkedInAdsClickId(searchParams);
  if (clickId) {
    try {
      window.sessionStorage.setItem(LINKEDIN_ADS_CLICK_ID_STORAGE_KEY, clickId);
    } catch {
      // The first-party attribution cookie remains available if session storage is unavailable.
    }
    setLinkedInAttributionCookie(clickId);
  } else {
    try {
      window.sessionStorage.removeItem(LINKEDIN_ADS_CLICK_ID_STORAGE_KEY);
    } catch {
      // Invalid attribution still clears the first-party cookie below.
    }
    clearLinkedInAttributionCookie();
  }
  removeLinkedInAdsClickIdFromCurrentUrl();
}

export function rewriteLinkedInAdsRequestUrl(rawUrl: string): string;
export function rewriteLinkedInAdsRequestUrl(rawUrl: URL): URL;
export function rewriteLinkedInAdsRequestUrl(rawUrl: string | URL): string | URL;
export function rewriteLinkedInAdsRequestUrl(rawUrl: string | URL) {
  try {
    const url = new URL(rawUrl.toString(), window.location.href);
    if (!isLinkedInRequestHost(url.hostname)) return rawUrl;

    const rewritten = sanitizeLinkedInRequestUrl(url, 0);
    if (!rewritten) return rawUrl;
    return rawUrl instanceof URL ? url : url.toString();
  } catch {
    return rawUrl;
  }
}

function sanitizeLinkedInRequestUrl(url: URL, depth: number): boolean {
  if (depth > MAX_NESTED_URL_DEPTH || !isLinkedInRequestHost(url.hostname)) return false;

  let rewritten = false;
  if (
    (url.pathname.includes("/collect") || url.pathname.includes("/attribution_trigger")) &&
    url.searchParams.has("url")
  ) {
    url.searchParams.set("url", RESTRICTED_PAGE_URL);
    rewritten = true;
  }
  if (url.searchParams.has("href")) {
    url.searchParams.set("href", RESTRICTED_PAGE_URL);
    rewritten = true;
  }

  for (const [name, value] of Array.from(url.searchParams.entries())) {
    if (name === "url" && value === RESTRICTED_PAGE_URL) continue;

    let nestedUrl: URL;
    try {
      nestedUrl = new URL(value);
    } catch {
      continue;
    }
    if (!sanitizeLinkedInRequestUrl(nestedUrl, depth + 1)) continue;

    url.searchParams.set(name, nestedUrl.toString());
    rewritten = true;
  }
  return rewritten;
}

function isLinkedInRequestHost(hostname: string): boolean {
  return (
    hostname === "linkedin.com" ||
    hostname.endsWith(".linkedin.com") ||
    hostname === "licdn.com" ||
    hostname.endsWith(".licdn.com")
  );
}

function normalizeNumericId(value: string): string | undefined {
  return /^[1-9]\d*$/.test(value) ? value : undefined;
}

function getStoredLinkedInAdsClickId(): string | undefined {
  try {
    const storedClickId = window.sessionStorage.getItem(LINKEDIN_ADS_CLICK_ID_STORAGE_KEY);
    if (storedClickId) {
      const validatedClickId = getLinkedInAdsClickId(
        new URLSearchParams([[LINKEDIN_ADS_CLICK_ID_QUERY_PARAM, storedClickId]]),
      );
      if (validatedClickId) return validatedClickId;
    }
  } catch {
    // Fall back to LinkedIn's first-party attribution cookie.
  }

  const cookieClickId = getAdsAttributionCookie(LINKEDIN_ADS_CLICK_ID_QUERY_PARAM);
  return cookieClickId ?
      getLinkedInAdsClickId(
        new URLSearchParams([[LINKEDIN_ADS_CLICK_ID_QUERY_PARAM, cookieClickId]]),
      )
    : undefined;
}

function clearStoredLinkedInAdsClickId() {
  try {
    window.sessionStorage.removeItem(LINKEDIN_ADS_CLICK_ID_STORAGE_KEY);
  } catch {
    // Consent revocation must not fail when storage is unavailable.
  }
}

function setLinkedInAttributionCookie(clickId: string) {
  setAdsAttributionCookie({
    name: LINKEDIN_ADS_CLICK_ID_QUERY_PARAM,
    value: clickId,
    maxAgeDays: LINKEDIN_CLICK_ID_COOKIE_MAX_AGE_DAYS,
  });
}

function ensureLinkedInAttributionCookie(clickId: string) {
  if (getAdsAttributionCookie(LINKEDIN_ADS_CLICK_ID_QUERY_PARAM) === clickId) return;
  setLinkedInAttributionCookie(clickId);
}

function restoreCurrentLinkedInAttributionCookie(deliveredClickId: string | undefined) {
  const currentClickId = getStoredLinkedInAdsClickId();
  if (currentClickId && currentClickId !== deliveredClickId) {
    setLinkedInAttributionCookie(currentClickId);
  }
}

function clearLinkedInAttributionCookie() {
  clearAdsAttributionCookie(LINKEDIN_ADS_CLICK_ID_QUERY_PARAM);
}

function clearLinkedInInsightTagState() {
  const configuredPartnerId = window._linkedin_partner_id;
  delete window._linkedin_partner_id;
  if (configuredPartnerId && window._linkedin_data_partner_ids) {
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids.filter(
      (id) => id !== configuredPartnerId,
    );
  }
  if (window.lintrk?.q) {
    window.lintrk.q = [];
  }
}

function removeLinkedInAdsClickIdFromCurrentUrl() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete(LINKEDIN_ADS_CLICK_ID_QUERY_PARAM);
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  } catch {
    // URL cleanup must not block conversion tracking.
  }
}

function initializeLinkedInInsightTag(configuredPartnerId: string): Lintrk {
  window._linkedin_partner_id = configuredPartnerId;
  window._linkedin_data_partner_ids ??= [];
  if (!window._linkedin_data_partner_ids.includes(configuredPartnerId)) {
    window._linkedin_data_partner_ids.push(configuredPartnerId);
  }

  if (window.lintrk) return window.lintrk;

  const lintrk = function linkedInInsightQueue(action: string, data: Record<string, unknown>) {
    lintrk.q?.push([action, data]);
  } as Lintrk;
  lintrk.q = [];
  window.lintrk = lintrk;
  return lintrk;
}

function loadLinkedInInsightScript(): Promise<boolean> {
  if (scriptLoaded) return Promise.resolve(true);
  if (scriptLoadPromise) return scriptLoadPromise;

  const existingScript = document.getElementById(LINKEDIN_INSIGHT_SCRIPT_ID);
  if (existingScript instanceof HTMLScriptElement) {
    scriptLoadPromise = waitForScriptLoad(existingScript);
    return scriptLoadPromise;
  }

  const script = document.createElement("script");
  script.id = LINKEDIN_INSIGHT_SCRIPT_ID;
  script.async = true;
  script.src = LINKEDIN_INSIGHT_SCRIPT_URL;
  scriptLoadPromise = waitForScriptLoad(script);
  document.head.appendChild(script);
  return scriptLoadPromise;
}

function waitForScriptLoad(script: HTMLScriptElement): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (loaded: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      resolve(loaded);
    };
    const timeoutId = window.setTimeout(() => {
      scriptLoadPromise = undefined;
      finish(false);
    }, LINKEDIN_SCRIPT_LOAD_TIMEOUT_MS);
    script.addEventListener(
      "load",
      () => {
        scriptLoaded = true;
        scriptLoadPromise = Promise.resolve(true);
        finish(true);
        void drainPendingLinkedInAdsConversions();
      },
      { once: true },
    );
    script.addEventListener(
      "error",
      () => {
        scriptLoadPromise = undefined;
        if (window.lintrk?.q) {
          window.lintrk.q = [];
        }
        script.remove();
        finish(false);
      },
      { once: true },
    );
  });
}

function isMeasurementReady(): boolean {
  return (
    measurementEnabled &&
    typeof window !== "undefined" &&
    partnerId != null &&
    accountCreationConversionId != null
  );
}

function shouldStopWaitingForMeasurementReady(): boolean {
  return consentResolved && !measurementEnabled;
}

function waitForMeasurementReady(): Promise<boolean> {
  if (isMeasurementReady()) return Promise.resolve(true);
  if (typeof window === "undefined" || shouldStopWaitingForMeasurementReady()) {
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (ready: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      measurementReadyWaiters = measurementReadyWaiters.filter((waiter) => waiter !== finish);
      resolve(ready);
    };
    const timeoutId = window.setTimeout(() => finish(false), LINKEDIN_MEASUREMENT_READY_TIMEOUT_MS);
    measurementReadyWaiters.push(finish);
  });
}

function notifyMeasurementStateChanged() {
  if (!isMeasurementReady() && !shouldStopWaitingForMeasurementReady()) return;

  const ready = isMeasurementReady();
  const waiters = measurementReadyWaiters;
  measurementReadyWaiters = [];
  for (const waiter of waiters) waiter(ready);
}
